import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import firebase from 'firebase/app';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';

import EnhancedTableHead from '../../components/EnhancedTableHead';
import EnhancedTableRow from '../../components/EnhancedTableRow';
import { getComparator, stableSort } from '../../modules/sort';
import SearchBox from '../../components/SearchBox';
import ContextStore from '../../modules/context';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 12 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const fields = [
  { name: 'nickname', sm: 6 },
  { name: 'type', type: 'select', sm: 6 },
  { name: 'tags', sm: 6 },
  { name: 'consumable', sm: 6 },

  // { name: 'cost', sm: 6 },
  { name: 'staffprice', sm: 6 },
  { name: 'staffRelativesPrice', sm: 6 },
  { name: 'vipprice', sm: 6 },
  { name: 'note', type: 'multiline', multiline: true, sm: 12, md: 12 },
  { name: 'owner', type: '-' },

  { name: 'doctorBonusType', type: 'bonus', sm: 6 },
  { name: 'salesBonusType', type: 'bonus', sm: 6 },
  { name: 'bonusType', type: 'bonus', sm: 6 },
  { name: 'operationBonus', sm: 6, md: 3 },

  { name: 'description', type: 'multiline', multiline: true, sm: 12, md: 12 },

].map(field => { field.multiline = field.multiline || false; field.md = field.md || 3; return field })

function ProductDetail({ product, merchandiseMapping }) {
  const { formatMessage } = useIntl()
  const openCode = product.code.substr(0, 1)
  const selectMapping = {
    type: [
      'AM', 'OP', 'SK', 'DP', 'OT', 'CN', 'EX', 'TT', 'GE', 'SU', 'ND', 'CU'
    ].reduce((acc, cur) => { acc[cur] = formatMessage({ id: `product.type.${cur}` }); return acc }, {}),
  }

  const headerCells = [
    { text: 'nickname' },
    { text: 'type' },
    { text: 'total' },
  ].map(c => { c.text = formatMessage({ id: `product.merchandise.detail.${c.text}` }); return c })

  const rowCells = [
    { field: 'nickname', tooltip: 'name' },
    { field: 'type' },
    { field: 'total' },
  ]

  const headerPriceCells = [
    { text: 'name' },
    { text: 'fixedAmount' },
    { text: 'useAmount' },
    { text: 'price' },
    { text: 'onShelfDate' },
    { text: 'offShelfDate' },
  ].map(c => { c.text = formatMessage({ id: `product.commonPrice.${c.text}` }); return c })

  const rowPriceCells = [
    { field: 'name' },
    { field: 'fixedAmount' },
    { field: 'useAmount' },
    { field: 'price' },
    { field: 'onShelfDate' },
    { field: 'offShelfDate' },
  ]

  const packageHeaderCells = openCode === '7' ? [
    { text: 'name' },
    { text: 'amount' },
    { text: 'price' },
    { text: 'lowestPrice' },
    // { text: 'cost' },
  ].map(c => { c.text = formatMessage({ id: `product.product.${c.text}` }); return c }) : [
    { text: 'name' },
    { text: 'amount' },
    { text: 'ratio' },
    // { text: 'cost' },
  ].map(c => { c.text = formatMessage({ id: `product.product.${c.text}` }); return c })

  const packageRowCells = openCode === '7' ? [
    { field: 'name' },
    { field: 'amount' },
    { field: 'price' },
    { field: 'lowestPrice' },
    // { field: 'cost' },
  ] : [
    { field: 'name' },
    { field: 'amount' },
    { field: 'ratio' },
    // { field: 'cost' },
  ]

  const formatData = (merchandise) => {
    const newData = { ...merchandise }
    newData.nickname = merchandiseMapping[newData.id].nickname
    newData.name = merchandiseMapping[newData.id].name
    newData.type = formatMessage({ id: `product.merchandise.type.${newData.type}` })
    newData.tags = (product.tags || []).join(', ')
    return newData
  }

  const packageFormatData = (data) => {
    const newData = { ...data }
    if (openCode !== '7') {
      newData.price = product.price * (newData.ratio / 100)
    }

    return newData
  }

  function createField(field, value, product) {
    let newValue = value
    if (!['1', '8', '9'].includes(openCode)) {
      if (['bonusType', 'doctorBonusType', 'salesBonusType', 'operationBonus'].includes(field.name)) {
        return
      }
    } else if (['9'].includes(openCode)) {
      if (['operationBonus'].includes(field.name)) {
        return
      }
    }

    if (field.type === 'select') {
      if (field.name === 'type') {
        newValue = selectMapping[field.name][value]
      } else {
        newValue = selectMapping[field.name][product.type][value]
      }
    } else if (field.type === 'bonus') {
      if (field.name === 'salesBonusType') {
        newValue = value?.name
      } else {
        if (value) {
          newValue = formatMessage({ id: `product.table.${field.name}.${value}` }) || ''
        }
      }
    }

    if (field.type === '-') {
      return <Grid item key={field.name} xs={12} sm={field.sm} md={12}><Divider /></Grid>
    }

    return <Grid item key={field.name} xs={12} sm={field.sm} md={field.md}>
      <TextField
        multiline={field.multiline}
        type="text"
        label={formatMessage({ id: `product.table.detail.${field.name}` })}
        value={field.name === 'consumable' ? value ? '療程消耗' : '直接銷售' : newValue}
        fullWidth
        variant="standard"
        size="small"
        margin="dense"
        InputProps={{
          readOnly: true,
        }}
      />
    </Grid>
  }

  return (
    <div style={{ padding: 15 }}>
      <Grid container spacing={2}>
        {fields.map(field => createField(field, product[field.name], product))}
        {product.commonPrice && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.commonPrice.title" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerPriceCells}
                rowCount={product.commonPrice.length}
              />
              <TableBody>
                {product.commonPrice.map(commonPrice => (
                  <EnhancedTableRow
                    key={commonPrice.id}
                    rowCells={rowPriceCells}
                    cellData={commonPrice}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {openCode !== '1' && product.package && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.products" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={packageHeaderCells}
                rowCount={product.package.length}
              />
              <TableBody>
                {product.package.map(m => packageFormatData(m)).map(packageProduct => (
                  <EnhancedTableRow
                    key={packageProduct.id}
                    rowCells={packageRowCells}
                    cellData={packageProduct}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {product.merchandiseType.length > 0 && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.merchandises" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerCells}
                rowCount={product.merchandiseType.length || [].length}
              />
              <TableBody>
                {product.merchandiseType.map(m => formatData(m)).map(merchandise => (
                  <EnhancedTableRow
                    key={merchandise.id}
                    rowCells={rowCells}
                    cellData={merchandise}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
      </Grid>
    </div>
  )
}

ProductDetail.propTypes = {
  product: PropTypes.object.isRequired,
  merchandiseMapping: PropTypes.object
};

function Products() {
  const { formatMessage } = useIntl()
  const { setBreadcrumbs } = useContext(ContextStore)
  const history = useHistory();
  const [productData, setProductData] = useState(null);
  const [productMapping, setProductMapping] = useState({})
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('code');
  const [currentFilter, setCurrentFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState({ tags: [] });
  const [merchandiseMapping, setMerchandiseMapping] = useState({})

  useEffect(() => {
    const ref = firebase.database().ref('merchandises')
    const onDataChange = ref.on('value', snapshot => {
      setMerchandiseMapping(snapshot.val())
    });
    return () => ref.off('value', onDataChange)
  }, []);

  useEffect(() => {
    const ref = firebase.database().ref('products')
    const onDataChange = ref.on('value', snapshot => {
      const data = []
      snapshot.forEach(doc => {
        const newData = { ...doc.val() }
        const newTags = newData.tags ? newData.tags.split(',') : []
        data.push({ id: doc.key, ...newData, type: newData.cat1, tags: newTags, })
      })

      setProductMapping(snapshot.val())
      setProductData(data)
    });
    return () => ref.off('value', onDataChange)
  }, []);

  useEffect(() => {
    setBreadcrumbs([{
      text: formatMessage({ id: 'sideMenu.products' })
    }])
    return () => {
    };
  }, [history.location.pathname]);

  if (!productData) {
    return 'Loading...'
  }

  const products = productData.reduce((acc, cur) => {
    acc.push({ ...cur, merchandiseType: merchandiseConcat(cur) })
    return acc
  }, [])

  const filteredproducts = filterproducts()
  const filteredTags = [...new Set(
    (categoryFilter.type ? products.filter(p => p.type.toLowerCase().includes(categoryFilter.type.toLowerCase())) : products).reduce((acc, cur) => acc.concat(cur.tags || []), [])
  )].sort()

  const headerCells = [
    { text: 'code', sort: 'code' },
    { text: 'name', sort: 'name' },
    { text: 'price', sort: 'price' },
    { text: 'lowestPrice', sort: 'lowestPrice' },
    { text: 'duration', sort: 'duration' },
    { text: 'onShelfDate', sort: 'onShelfDate' },
    { text: 'offShelfDate', sort: 'offShelfDate' },
  ].map(c => { c.text = formatMessage({ id: `product.table.header.${c.text}` }); return c })

  const rowCells = [
    { field: 'code' },
    { field: 'name' },
    { field: 'price' },
    { field: 'lowestPrice' },
    { field: 'duration' },
    { field: 'onShelfDate' },
    { field: 'offShelfDate' },
  ]

  const formatData = (product) => {
    const newData = { ...product }
    const newPackage = []
    const newCommonPrice = []
    if (newData.commonPrice) {
      for (const x of Object.keys(newData.commonPrice)) {
        newCommonPrice.push({ id: x, ...newData.commonPrice[x] })
      }

      newData.commonPrice = newCommonPrice
    }

    if (newData.package) {
      for (const p of Object.keys(newData.package)) {
        newPackage.push({
          id: p,
          ...newData.package[p],
          name: productMapping[p]?.name,
          cost: productMapping[p]?.cost
        })
      }

      newData.package = newPackage
    }

    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filterItems = [
    { name: 'name' },
    { name: 'code' },
  ].map(i => { i.text = formatMessage({ id: `product.table.detail.${i.name}` }); return i })

  const categoryFilterItems = [
    { name: 'type' },
    { name: 'tags' },
  ].map(i => { i.text = formatMessage({ id: `product.table.detail.${i.name}` }); return i })

  function filterproducts() {
    if (!currentFilter && !categoryFilter.type && categoryFilter.tags.length === 0) {
      return products
    }
    let items = [...products]
    if (categoryFilter.type) {
      const type = categoryFilter.type.toUpperCase()
      items = items.filter(i => i.type === type)
    }
    if (categoryFilter.tags.length > 0) {
      items = items.filter(i => categoryFilter.tags.every(tag => (i.tags || []).includes(tag)))
    }
    if (currentFilter) {
      items = items.filter(s => s[currentFilter.name].toLowerCase().includes(currentFilter.text.toLowerCase()))
    }
    return items
  }

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  function getMenuItem(name) {
    if (name === 'type') {
      return [
        { value: '' },
        { value: 'beauty', header: true }, { value: 'AM' }, { value: 'OP' }, { value: 'SK' }, { value: 'DP' },
        { value: 'preventative', header: true }, { value: 'CN' }, { value: 'EX' }, { value: 'TT' }, { value: 'GE' }, { value: 'SU' },
        { value: 'ND' }, { value: 'CU' },
        { value: 'other', header: true }, { value: 'OT' }
      ].map(i => {
        i.label = i.value ? formatMessage({ id: `product.type.${i.value}` }) : formatMessage({ id: 'product.category.all' })
        return i
      })
    }
    return []
  }

  function merchandiseConcat(product) {

    const merchandises = Object.keys(product.merchandises || {}).map(key => ({
      ...product.merchandises[key],
      id: key,
      type: 'merchandises'
    }))

    const adjustables = Object.keys(product.adjustables || {}).map(key => ({
      ...product.adjustables[key],
      id: key,
      type: 'adjustables'
    }))

    return merchandises.concat(adjustables)
  }

  function updateCategoryFilter(name, value) {
    const newFilter = { ...categoryFilter }
    newFilter[name] = value

    setCategoryFilter(newFilter)
  }

  const getFilter = (filter) => {
    if (filter.name === 'tags') {
      return <Grid item key={filter.name} xs={6} sm={6} md={5}>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel id="demo-multiple-checkbox-label">{formatMessage({ id: 'product.table.detail.tags' })}</InputLabel>
          <Select
            labelId="demo-mutiple-checkbox-label"
            id="demo-mutiple-textbox"
            multiple
            value={categoryFilter.tags}
            onChange={e => { updateCategoryFilter(filter.name, e.target.value) }}
            input={<OutlinedInput label={formatMessage({ id: 'product.table.detail.tags' })} />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {filteredTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                <Checkbox checked={categoryFilter.tags.includes(tag)} />
                <ListItemText primary={tag} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    } else {
      return <Grid item key={filter.name} xs={6} sm={6} md={3}>
        <TextField
          select
          type="text"
          size="small"
          label={filter.text}
          variant="outlined"
          onChange={e => { updateCategoryFilter(filter.name, e.target.value) }}
          value={categoryFilter[filter.name] || ''}
          fullWidth
        >
          {getMenuItem(filter.name).map((option, idx) => {
            return option.header ?
              (<ListSubheader key={`${option.value}-${idx}`}>{option.label}</ListSubheader>) :
              (<MenuItem key={`${option.value}-${idx}`} value={option.value}>{option.label}</MenuItem>)
          })}
        </TextField>
      </Grid>
    }
  }

  return (
    <div style={{ padding: '40px 20px' }}>
      <Toolbar sx={{ pl: '16px', pr: '8px' }}>
        <Typography sx={{ flex: '1 1 100%', flexShrink: 2 }} variant="h6" id="tableTitle" component="div">
          <FormattedMessage id="product.table.title" />
        </Typography>
        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
          {categoryFilterItems.map(f => getFilter(f))}
          <Grid item xs={6} sm={6} md={4}>
            <SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} />
          </Grid>
        </Grid>
      </Toolbar>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <EnhancedTableHead
            headerCells={headerCells}
            order={order}
            orderBy={orderBy}
            expandable
            onRequestSort={handleRequestSort}
            rowCount={filteredproducts.length}
          />
          <TableBody>
            {stableSort(filteredproducts, getComparator(order, orderBy)).map(product => (
              <EnhancedTableRow
                key={product.id}
                rowCells={rowCells}
                cellData={product}
                expandable
                expandContent={<ProductDetail
                  product={formatData(product)}
                  merchandiseMapping={merchandiseMapping}
                />}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div >
  );
}

export default Products;
